import { CommonModule } from "@angular/common";
import {
    Component,
    computed,
    effect,
    inject,
    input,
    Input,
    signal
} from "@angular/core";
import { MarginLeftDirective } from "@app/shared/directives/margin-left.directive";
import { NumberFormatTooltipDirective } from "@app/shared/directives/number-format-tooltip.directive";
import { SharedDsnModule } from "@app/shared/modules/shared-dsn.module";
import { SharedMaterialModule } from "@app/shared/modules/shared-material.module";
import { VisualizationOptionsType } from "@arch/_shared/types/options-visualization";
import { DateUtils } from "@arch/_shared/utils";
import { DRESerieData } from "@arch/domain/application/usecase";
import { CompanyEntity, ScenarioEntity } from "@arch/domain/entity";
import { GetDreDataController } from "@arch/presentation/controllers";

@Component({
    selector: "app-table-dre-complete",
    imports: [
        SharedMaterialModule,
        SharedDsnModule,
        NumberFormatTooltipDirective,
        CommonModule,
        MarginLeftDirective
    ],

    templateUrl: "./table-dre-complete.component.html",
    styleUrl: "./table-dre-complete.component.scss"
})
export class TableDreCompleteComponent {
    @Input({ required: true }) company: CompanyEntity;
    @Input() set options(value: VisualizationOptionsType) {
        this.optionsSignal.set(value);
    }
    get options() {
        return this.optionsSignal();
    }
    @Input({ required: false }) pdf: boolean;
    @Input({ required: false }) dates: Date[];
    @Input({ required: false }) indexInterval: number[];
    @Input({ required: false }) tabActive: "period" | "accumulated" = "period";

    public loading = input.required<boolean>();
    public optionsSignal = signal<VisualizationOptionsType>(null);

    private readonly getDreDataController = inject(GetDreDataController);

    public rows: DRESerieData[] = [];
    public loadingRows = new Array(12);

    constructor() {
        effect(async (): Promise<void> => {
            if (!this.options || this.loading()) return null;
            this.tabActive = this.pdf ? this.tabActive : "period";
            const dates = this.columns();
            let seriesData = await this.getDreDataController.execute({
                options: this.options,
                company: this.company,
                dates: dates.map((d) => d.date),
                optionsConfig: {
                    verticalAnalysisReference:
                        this.options.dre.verticalReference,
                    calcAnalysisVertical: this.options.dre.analysisVertical,
                    calcAnalysisHorizontal: this.options.dre.analysisHorizontal
                }
            });
            if (this.company.isGroup) {
                seriesData = seriesData.filter(
                    (s) =>
                        s.serie.props.level == 0 ||
                        s.serie.props.level === undefined
                );
            }
            if (this.indexInterval) {
                this.rows = seriesData.filter((data, index) =>
                    this.indexInterval.includes(index)
                );
            } else {
                this.rows = [...seriesData];
            }
        });
    }

    public labelPeriod = computed((): string => {
        if (!this.options) return "";
        const startDate = DateUtils.formatByPeriodName(
            this.options.comparation.active
                ? this.options.comparation.period
                : this.options.period,
            this.options.comparation.active
                ? this.options.endDate
                : this.options.startDate,
            true
        );
        const endDate = DateUtils.formatByPeriodName(
            this.options.period,
            this.options.comparation.active
                ? this.options.comparation.date
                : this.options.endDate,
            true
        );
        return `${startDate} - ${endDate}`;
    });

    public labelPeriodPdf = computed((): string => {
        if (!this.options) return "";
        const startDate = DateUtils.formatByPeriodName(
            this.options.period,
            this.dates[0],
            true
        );
        const endDate = DateUtils.formatByPeriodName(
            this.options.period,
            this.dates[this.dates.length - 1],
            true
        );
        return `DRE ${startDate} - ${endDate}`;
    });

    public columns = computed(
        (): {
            dateFormatted: string;
            date: Date;
            scenario: ScenarioEntity;
        }[] => {
            if (!this.options || this.loading()) return [];
            if (this.pdf && this.dates) {
                return this.dates.map((date) => ({
                    dateFormatted: DateUtils.formatByPeriodName(
                        this.options.period,
                        date,
                        true
                    ),
                    date,
                    scenario: this.options.scenario
                }));
            }
            if (this.options.comparation.active) {
                const datesInterval = [
                    {
                        date: this.options.endDate,
                        scenario: this.options.scenario
                    },
                    {
                        date: this.options.comparation.date,
                        scenario: this.options.comparation.scenario
                    }
                ];
                return datesInterval.map((date) => ({
                    dateFormatted: DateUtils.formatByPeriodName(
                        this.options.period,
                        date.date,
                        true
                    ),
                    date: date.date,
                    scenario: date.scenario
                }));
            }
            const datesInterval = DateUtils.getDatesInterval(
                this.options.startDate,
                this.options.endDate,
                this.options.period
            );
            return datesInterval.map((date) => ({
                dateFormatted: DateUtils.formatByPeriodName(
                    this.options.period,
                    date,
                    true
                ),
                date,
                scenario: this.options.scenario
            }));
        }
    );

    public onChangeTab(tab: "period" | "accumulated"): void {
        if (this.options.comparation.active && tab === "accumulated")
            return null;
        this.tabActive = tab;
    }

    public getRowAnimationStyle(index: number): { [key: string]: string } {
        return {
            "--row-index": index.toString()
        };
    }

    public getProgressClasses(avValue: number): string {
        return (avValue || 0) >= 100 ? "full-width" : "";
    }

    public getProgressStyle(avValue: number): { [key: string]: string } {
        return {
            "--progress-width": `${avValue || 0}%`
        };
    }
}
